import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ExameDetalhes.css';

import { API_URL } from '../config';
import { formatDate, formatDateTime, formatTime, formatDateOfBirth } from '../utils/formatDate';

function AtendimentoDetalhes() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [atendimento, setAtendimento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('documento');
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchAtendimentoDetalhes();
  }, [id]);

  const fetchAtendimentoDetalhes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/atendimentos/${id}`);
      setAtendimento(response.data);
    } catch (err) {
      setError('Erro ao carregar dados do atendimento: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!atendimento?.LOCAL || !atendimento?.PROTOCOLO) return;
    setDownloadingPdf(true);
    try {
      const local = atendimento.LOCAL.trim();
      const protocolo = atendimento.PROTOCOLO;
      const response = await axios.get(`${API_URL}/laudos/download/${local}/${protocolo}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `laudo_${local}${protocolo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      let msg = 'Erro ao baixar PDF do laudo';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          msg = json.error || json.message || msg;
        } catch {}
      } else if (error.response?.data) {
        msg = error.response.data.error || error.response.data.message || msg;
      }
      alert(msg);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const formatCPF = (cpf) => {
    if (!cpf) return '-';
    const cleaned = cpf.toString().replace(/\D/g, '');
    if (cleaned.length !== 11) return cpf;
    return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    const cleaned = phone.toString().replace(/\D/g, '');
    if (cleaned.length === 11) {
      return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    } else if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
  };

  const formatCEP = (cep) => {
    if (!cep) return '-';
    const cleaned = cep.toString().replace(/\D/g, '');
    if (cleaned.length !== 8) return cep;
    return cleaned.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const formatMoney = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return '-';
    return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  // formatDate, formatDateOfBirth importados de ../utils/formatDate

  const formatProtocolo = (protocolo) => {
    if (!protocolo) return '';
    const protocoloStr = protocolo.toString();
    // Preencher com zeros à esquerda até 6 dígitos
    return protocoloStr.padStart(6, '0');
  };

  // formatDateTime, formatTime importados de ../utils/formatDate

  const formatarValor = (key, value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    // Arrays e objetos
    if (Array.isArray(value)) {
      return value.length > 0 ? `${value.length} item(ns)` : '-';
    }
    if (typeof value === 'object') {
      return 'Ver detalhes';
    }

    // Campos específicos que precisam formatação especial
    if (key === 'PACIENTE_CPF') return formatCPF(value);
    if (key === 'PACIENTE_TELEFONE') return formatPhone(value);
    if (key === 'PACIENTE_CEP') return formatCEP(value);
    if (key === 'PACIENTE_DATANASCIMENTO') return formatDateOfBirth(value);
    if (key.includes('DATA') && key !== 'DATAINCLUSAO_PAGAMENTO') return formatDate(value);
    if (key.includes('HORARIO')) return formatTime(value);
    if (['TOTAL', 'DESCONTO', 'VALOR_BRUTO', 'VALOR_LIQUIDO', 'TAXAEXTRA'].includes(key)) return formatMoney(value);

    // Campos de exames que precisam ser exibidos em lista
    if (key === 'EXAMES' || key === 'SIGLAS') {
      // Separador: múltiplos espaços (2 ou mais)
      const items = value
        .replace(/[\r\n]+/g, ' ')  // Remove quebras de linha
        .split(/\s{2,}/)            // Split por 2+ espaços
        .map(item => item.trim())
        .filter(item => item);      // Remove vazios

      if (items.length === 0) return '-';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {items.map((item, idx) => (
            <div key={idx} style={{
              padding: '0.25rem 0.5rem',
              background: '#f9fafb',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              • {item}
            </div>
          ))}
        </div>
      );
    }

    if (key === 'SIGLASEXAME') {
      // Separador: vírgula
      const items = value
        .split(',')
        .map(item => item.trim())
        .filter(item => item);

      if (items.length === 0) return '-';

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          {items.map((item, idx) => (
            <div key={idx} style={{
              padding: '0.25rem 0.5rem',
              background: '#f9fafb',
              borderRadius: '4px',
              fontSize: '0.85rem'
            }}>
              • {item}
            </div>
          ))}
        </div>
      );
    }

    // Booleanos e strings T/F
    if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    }
    if (typeof value === 'string') {
      if (value === 'T' || value === 't') return 'Sim';
      if (value === 'F' || value === 'f') return 'Não';
      if (value === 'M') return 'Masculino';
      if (value === 'F') return 'Feminino';
      return value;
    }

    return value.toString();
  };

  if (loading) {
    return <div className="loading">Carregando detalhes do atendimento...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error">{error}</p>
        <button onClick={() => navigate('/atendimentos')} className="back-button">
          Voltar para Atendimentos
        </button>
      </div>
    );
  }

  if (!atendimento) {
    return <div className="loading">Atendimento não encontrado</div>;
  }

  // Mapa de nomes legíveis para campos do banco
  const labelMap = {
    'ID': 'Código', 'PROTOCOLO': 'Protocolo', 'DATA': 'Data',
    'DATAENTREGA': 'Data de Entrega', 'USUARIO': 'Usuário',
    'PACIENTE': 'Prontuário', 'PACIENTE_NOME': 'Nome do Paciente',
    'PACIENTE_CPF': 'CPF', 'PACIENTE_DATANASCIMENTO': 'Data de Nascimento',
    'PACIENTE_SEXO': 'Sexo', 'PACIENTE_TELEFONE': 'Telefone',
    'PACIENTE_EMAIL': 'E-mail', 'IDADE_PACIENTE': 'Idade',
    'PACIENTE_ENDERECO': 'Endereço', 'PACIENTE_NUMERO': 'Número',
    'PACIENTE_COMPLEMENTO': 'Complemento', 'PACIENTE_BAIRRO': 'Bairro',
    'PACIENTE_CEP': 'CEP', 'PACIENTE_CIDADE': 'Cidade', 'PACIENTE_UF': 'UF',
    'PESO': 'Peso', 'ALTURA': 'Altura', 'CID': 'CID', 'LEITO': 'Leito',
    'GESTACAO': 'Gestação', 'DATAULTMENS': 'Dt. Última Menstruação',
    'MEDICAMENTOS': 'Medicamentos', 'DUM': 'DUM', 'INDICACAO_CLINICA': 'Indicação Clínica',
    'TIPO_ATENDIMENTO': 'Tipo de Atendimento', 'SITUACAO_ATENDIMENTO': 'Situação',
    'CARATER_SOLICITACAO': 'Caráter Solicitação', 'FORMA_ENTREGA': 'Forma de Entrega',
    'MEDICO': 'Médico', 'MEDICO_SOLICITANTE': 'Médico Solicitante',
    'CRM': 'CRM', 'CRM_SOLICITANTE': 'CRM Solicitante',
    'UF_CRM': 'UF do CRM', 'CONSELHO': 'Conselho',
    'TOTAL': 'Total', 'DESCONTO': 'Desconto',
    'VALOR_BRUTO': 'Valor Bruto', 'VALOR_LIQUIDO': 'Valor Líquido',
    'CONVENIO': 'Convênio', 'PLANO': 'Plano', 'CARTEIRINHA': 'Carteirinha',
    'VALIDADE_CARTAO': 'Val. Cartão', 'NUMERO_GUIA': 'Nº Guia',
    'GUIA_PRINCIPAL': 'Guia Principal', 'TAXAEXTRA': 'Taxa Extra',
    'EXAMES': 'Exames', 'SIGLAS': 'Siglas', 'SIGLASEXAME': 'Siglas dos Exames',
    'LOCAL': 'Local', 'LOCALP': 'Local P', 'PROTOCOLOP': 'Protocolo P',
    'HORARIO': 'Horário', 'HORARIOENTREGA': 'Horário Entrega',
    'SUBTOTAL': 'Subtotal', 'USUARIO_INCLUSAO': 'Usuário Inclusão',
    'SENHA_SLINE': 'Senha S-Line', 'RESULTADO_LIBERADO': 'Resultado Liberado',
    'ENVIAR_NOTIFICACAO_EMAIL': 'Enviar Notificação', 'ENVIAR_SMS': 'Enviar SMS',
    'ORIGEM': 'Origem', 'NUMERO_ENDERECO': 'Nº Endereço',
    'OBSERVACAO1': 'Observação 1', 'OBSERVACAO2': 'Observação 2',
    'OBSERVACAO3': 'Observação 3', 'OBS_RECIBO': 'Obs. Recibo',
    'OBS_FATURAMENTO': 'Obs. Faturamento', 'INDIC_CLINICA': 'Indicação Clínica',
    'ATENDIMENTO_DOMICILIAR': 'Domiciliar', 'IMPRIME_REQ': 'Imprime Requisição',
    'ENVIA_SLINE_PAC': 'Envia S-Line Paciente', 'NUMEROETIQUETA': 'Nº Etiqueta',
    'CARATER_ATENDIMENTO': 'Caráter', 'JEJUM': 'Jejum',
    'ULTIMA_REFEICAO': 'Última Refeição',
  };

  const getLabel = (key) => labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  // Categorizar campos do atendimento
  const categorias = {
    'Identificação': ['ID', 'PROTOCOLO', 'DATA', 'DATAENTREGA', 'USUARIO'],
    'Dados do Paciente': [
      'PACIENTE', 'PACIENTE_NOME', 'PACIENTE_CPF', 'PACIENTE_DATANASCIMENTO',
      'PACIENTE_SEXO', 'PACIENTE_TELEFONE', 'PACIENTE_EMAIL',
      'IDADE_PACIENTE'
    ],
    'Endereço do Paciente': [
      'PACIENTE_ENDERECO', 'PACIENTE_NUMERO', 'PACIENTE_COMPLEMENTO',
      'PACIENTE_BAIRRO', 'PACIENTE_CEP', 'PACIENTE_CIDADE', 'PACIENTE_UF'
    ],
    'Dados Clínicos': [
      'PESO', 'ALTURA', 'CID', 'LEITO', 'GESTACAO', 'DATAULTMENS',
      'MEDICAMENTOS', 'DUM', 'INDICACAO_CLINICA'
    ],
    'Tipo de Atendimento': [
      'TIPO_ATENDIMENTO', 'SITUACAO_ATENDIMENTO', 'CARATER_SOLICITACAO',
      'FORMA_ENTREGA'
    ],
    'Médico Solicitante': [
      'MEDICO', 'MEDICO_SOLICITANTE', 'CRM', 'CRM_SOLICITANTE',
      'UF_CRM', 'CONSELHO'
    ],
    'Valores': [
      'TOTAL', 'DESCONTO', 'VALOR_BRUTO', 'VALOR_LIQUIDO'
    ],
    'Convênio': [
      'CONVENIO', 'PLANO', 'CARTEIRINHA', 'VALIDADE_CARTAO',
      'NUMERO_GUIA', 'GUIA_PRINCIPAL'
    ]
  };

  const camposCategorizados = {};
  const camposUsados = new Set();

  // Categorizar campos existentes
  Object.entries(categorias).forEach(([categoria, campos]) => {
    const camposEncontrados = campos.filter(campo =>
      atendimento.hasOwnProperty(campo) && !camposUsados.has(campo)
    );
    if (camposEncontrados.length > 0) {
      camposCategorizados[categoria] = camposEncontrados;
      camposEncontrados.forEach(campo => camposUsados.add(campo));
    }
  });

  // Adicionar campos não categorizados
  const camposNaoCategorizados = Object.keys(atendimento)
    .filter(key => !camposUsados.has(key) && key !== 'exames');
  if (camposNaoCategorizados.length > 0) {
    camposCategorizados['Outros'] = camposNaoCategorizados;
  }

  // Definir abas disponíveis
  const abas = [
    { id: 'documento', label: '📄 Documento', show: true },
    { id: 'dados', label: '📋 Dados do Atendimento', show: true },
    { id: 'exames', label: '🧪 Exames', show: atendimento.EXAMES },
    { id: 'guias', label: '📑 Guias', show: atendimento.guias && atendimento.guias.length > 0 },
    { id: 'pagamentos', label: '💰 Pagamentos', show: true },
    { id: 'comunicacoes', label: '💬 Comunicações', show: atendimento.comunicacoes && atendimento.comunicacoes.length > 0 },
    { id: 'laudos', label: '📑 Laudos', show: atendimento.laudos && atendimento.laudos.length > 0 }
  ].filter(aba => aba.show);

  return (
    <div className="exame-detalhes-page">
      <div className="page-header-detalhes">
        <button onClick={() => navigate('/atendimentos')} className="back-button">
          ← Voltar
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1>Detalhes do Atendimento</h1>
          {atendimento.estatisticas?.statusPagamento && (() => {
            const statusMap = {
              PAGO: { label: 'Pago', bg: '#10b981', color: '#fff' },
              NAO_PAGO: { label: 'Nao Pago', bg: '#ef4444', color: '#fff' },
              PARCIAL: { label: 'Parcial', bg: '#f59e0b', color: '#fff' },
              EXCEDENTE: { label: 'Excedente', bg: '#8b5cf6', color: '#fff' },
              ISENTO: { label: 'Isento', bg: '#6b7280', color: '#fff' }
            };
            const s = statusMap[atendimento.estatisticas.statusPagamento] || statusMap.NAO_PAGO;
            return (
              <span style={{
                background: s.bg, color: s.color, padding: '4px 12px',
                borderRadius: '12px', fontSize: '0.8rem', fontWeight: 'bold'
              }}>
                {s.label}
              </span>
            );
          })()}
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {atendimento.PROTOCOLO && (
            <button
              onClick={() => navigate(`/protocolo/${atendimento.PROTOCOLO}`)}
              className="btn-detail-primary"
            >
              Ver Protocolo {atendimento.PROTOCOLO}
            </button>
          )}
          <button
            onClick={() => navigate(`/resultados/${atendimento.ID}`)}
            className="btn-detail-success"
          >
            Ver Resultados
          </button>
          {atendimento.LOCAL && atendimento.PROTOCOLO && atendimento.RESULTADO_LIBERADO === 'T' && (
            <button
              onClick={handleDownloadPdf}
              disabled={downloadingPdf}
              className="btn-detail-warning"
            >
              {downloadingPdf ? 'Baixando...' : 'Baixar PDF'}
            </button>
          )}
        </div>
      </div>

      {/* Sistema de Abas */}
      <div className="detail-tabs">
        {abas.map(aba => (
          <button
            key={aba.id}
            onClick={() => setAbaAtiva(aba.id)}
            className={`detail-tab ${abaAtiva === aba.id ? 'active' : ''}`}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Comprovante de Atendimento */}
      {abaAtiva === 'documento' && (
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        border: '2px solid #e5e7eb',
        fontFamily: 'monospace'
      }}>
        {/* Cabeçalho do Laboratório */}
        <div style={{
          borderBottom: '2px dashed #000',
          paddingBottom: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>LABORATÓRIO SANTA RITA</h2>
              <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>Cuidado e confiança refletido em cada resultado!</p>
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.8rem' }}>
              <div>Av. Ribeiro Pena, 196 - Centro - Itapecerica</div>
              <div>Fone: (37) 3341-1460</div>
              <div>WhatsApp: (37) 99835-7851</div>
            </div>
          </div>
        </div>

        {/* Informações do Cliente */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
            <div>
              <strong>Cliente: {atendimento.PACIENTE_NOME || '-'}</strong>
            </div>
            <div>
              <strong>Identidade:</strong> {atendimento.PACIENTE_CPF || '-'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1rem', marginBottom: '0.5rem' }}>
            <div>
              <strong>Sexo/Idade:</strong> {atendimento.PACIENTE_SEXO || '-'}/{atendimento.IDADE_PACIENTE || '-'}
            </div>
            <div>
              <strong>DN:</strong> {formatDateOfBirth(atendimento.PACIENTE_DATANASCIMENTO)}
            </div>
          </div>
          {(atendimento.PACIENTE_ENDERECO || atendimento.PACIENTE_BAIRRO || atendimento.PACIENTE_CIDADE) && (
            <div style={{ fontSize: '0.85rem', color: '#444' }}>
              <strong>Endereço:</strong> {[
                atendimento.PACIENTE_ENDERECO,
                atendimento.NUMERO_ENDERECO ? `Nº ${atendimento.NUMERO_ENDERECO}` : null,
                atendimento.PACIENTE_COMPLEMENTO,
                atendimento.PACIENTE_BAIRRO,
                atendimento.PACIENTE_CIDADE && atendimento.PACIENTE_UF
                  ? `${atendimento.PACIENTE_CIDADE}/${atendimento.PACIENTE_UF}`
                  : atendimento.PACIENTE_CIDADE,
                atendimento.PACIENTE_CEP ? `CEP: ${atendimento.PACIENTE_CEP}` : null
              ].filter(Boolean).join(', ')}
            </div>
          )}
        </div>

        {/* Dados do Atendimento */}
        <div style={{
          background: '#f3f4f6',
          padding: '1rem',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', fontSize: '0.9rem' }}>
            <div>
              <strong>Atendimento:</strong> {atendimento.ID}
            </div>
            <div>
              <strong>Data:</strong> {formatDate(atendimento.DATA)}
            </div>
            <div>
              <strong>Atendente:</strong> {atendimento.USUARIO || '-'}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginTop: '0.5rem', fontSize: '0.9rem' }}>
            <div>
              <strong>Protocolo:</strong> {atendimento.PROTOCOLO}
            </div>
            <div>
              <strong>Prontuário:</strong> {atendimento.PACIENTE}
            </div>
            <div>
              <strong>Convênio:</strong> {atendimento.CONVENIO || 'PARTICULAR'}
            </div>
          </div>
        </div>

        {/* Resultados Online */}
        {atendimento.SENHA_SLINE && (() => {
          const local = String(atendimento.LOCALP || atendimento.LOCAL || '').trim();
          const proto = formatProtocolo(atendimento.PROTOCOLOP || atendimento.PROTOCOLO);
          const codigoB64 = btoa(local + proto);
          const senhaB64 = btoa(String(atendimento.SENHA_SLINE));
          const linkDireto = `http://app.lifesys.com.br/laudos/#/loginAutolac?codigo=${codigoB64}&senha=${senhaB64}`;
          return (
            <div style={{
              border: '2px solid #3b82f6',
              padding: '1rem',
              borderRadius: '4px',
              marginBottom: '1rem',
              background: '#eff6ff'
            }}>
              <div style={{
                fontWeight: 'bold',
                marginBottom: '0.5rem',
                fontSize: '0.9rem',
                color: '#1e40af'
              }}>
                📱 RESULTADOS ON-LINE
              </div>
              <div style={{ fontSize: '0.85rem', lineHeight: '1.8' }}>
                <div>
                  <strong>Código:</strong> {local}{proto} &nbsp;
                  <strong>Senha:</strong> {atendimento.SENHA_SLINE}
                </div>
                <div style={{ marginTop: '0.5rem' }}>
                  <a
                    href={linkDireto}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#1e40af', textDecoration: 'underline', wordBreak: 'break-all' }}
                  >
                    Abrir resultado direto
                  </a>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Exames Agrupados */}
        {atendimento.EXAMES && (
          <div style={{ marginBottom: '1rem' }}>
            <div style={{
              borderBottom: '1px solid #ddd',
              paddingBottom: '0.5rem',
              marginBottom: '0.5rem',
              display: 'grid',
              gridTemplateColumns: '2fr 1fr',
              fontWeight: 'bold',
              fontSize: '0.85rem'
            }}>
              <div>Exame(s)</div>
              <div>Coleta</div>
            </div>
            <div style={{ fontSize: '0.85rem', lineHeight: '1.6' }}>
              {(() => {
                // Criar mapa de código do exame -> data de coleta
                const examesMap = {};
                if (atendimento.exames && atendimento.exames.length > 0) {
                  atendimento.exames.forEach(ex => {
                    examesMap[ex.EXAME] = {
                      dataColeta: ex.DATACOLETA,
                      horaColeta: ex.HORACOLETA
                    };
                  });
                }

                return atendimento.EXAMES
                  .replace(/[\r\n]+/g, ' ')  // Remove quebras de linha
                  .split(/\s{2,}/)           // Split por 2+ espaços
                  .filter(e => e.trim())     // Remove vazios
                  .map((exameTexto, idx) => {
                    const exameNome = exameTexto.trim();

                    // Tentar encontrar o código do exame
                    // Estratégia 1: Primeira palavra (código)
                    const palavras = exameNome.split(/[\s,]+/);
                    const codigo = palavras[0];
                    let exameInfo = examesMap[codigo];

                    // Estratégia 2: Se não encontrou, tentar buscar por correspondência parcial
                    if (!exameInfo) {
                      Object.keys(examesMap).forEach(key => {
                        if (exameNome.includes(key) || key.includes(codigo)) {
                          exameInfo = examesMap[key];
                        }
                      });
                    }

                    // Estratégia 3: Usar índice do array se disponível
                    if (!exameInfo && atendimento.exames && atendimento.exames[idx]) {
                      exameInfo = {
                        dataColeta: atendimento.exames[idx].DATACOLETA,
                        horaColeta: atendimento.exames[idx].HORACOLETA
                      };
                    }

                    return (
                      <div key={idx} style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr',
                        padding: '0.15rem 0',
                        gap: '0.5rem'
                      }}>
                        <div>• {exameNome}</div>
                        <div>{exameInfo ? formatDate(exameInfo.dataColeta) : '-'}</div>
                      </div>
                    );
                  });
              })()}
            </div>
          </div>
        )}

        {/* Valores */}
        <div style={{
          borderTop: '2px dashed #000',
          paddingTop: '1rem',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '1rem',
          fontSize: '0.9rem'
        }}>
          <div>
            <strong>Total exame(s):</strong>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
              {atendimento.EXAMES
                ? atendimento.EXAMES.replace(/[\r\n]+/g, ' ').split(/\s{2,}/).filter(e => e.trim()).length
                : 0
              }
            </div>
          </div>
          <div>
            <strong>Valor a pagar:</strong>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>R$ {(atendimento.TOTAL || 0).toFixed(2)}</div>
          </div>
          <div>
            <strong>Valor pago:</strong>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#10b981' }}>
              R$ {atendimento.estatisticas?.valorTotalPago?.toFixed(2) || '0.00'}
            </div>
            {atendimento.estatisticas?.statusPagamento && (() => {
              const statusMap = {
                PAGO: { label: 'Pago', color: '#10b981' },
                NAO_PAGO: { label: 'Nao Pago', color: '#ef4444' },
                PARCIAL: { label: 'Parcial', color: '#f59e0b' },
                EXCEDENTE: { label: 'Excedente', color: '#8b5cf6' },
                ISENTO: { label: 'Isento', color: '#6b7280' }
              };
              const s = statusMap[atendimento.estatisticas.statusPagamento] || statusMap.NAO_PAGO;
              return <div style={{ fontSize: '0.8rem', color: s.color, fontWeight: 'bold' }}>{s.label}</div>;
            })()}
          </div>
        </div>

        {/* Observações */}
        <div style={{
          marginTop: '1rem',
          fontSize: '0.75rem',
          color: '#666',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '0.5rem'
        }}>
          <div>A previsão de entrega do seu resultado é: {formatDate(atendimento.DATAENTREGA)}</div>
          <div>Os resultados estarão disponíveis no laboratório no período de 60 dias</div>
        </div>
      </div>
      )}

      {/* Aba: Dados do Atendimento */}
      {abaAtiva === 'dados' && (
      <>
      {/* Estatísticas do Atendimento */}
      {atendimento.estatisticas && (
        <div className="stats-summary-section" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div className="stat-card">
            <div className="stat-label">Exames</div>
            <div className="stat-value">
              {atendimento.EXAMES
                ? atendimento.EXAMES.replace(/[\r\n]+/g, ' ').split(/\s{2,}/).filter(e => e.trim()).length
                : 0
              }
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Guias</div>
            <div className="stat-value">{atendimento.estatisticas.totalGuias}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pagamentos</div>
            <div className="stat-value">{atendimento.estatisticas.totalPagamentos}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Comunicacoes</div>
            <div className="stat-value">{atendimento.estatisticas.totalComunicacoes}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Laudos</div>
            <div className="stat-value">{atendimento.estatisticas.totalLaudos}</div>
          </div>
        </div>
      )}

      <div className="detalhes-grid">
        {Object.entries(camposCategorizados).map(([categoria, campos]) => (
          <div key={categoria} className="info-card">
            <h3>📋 {categoria}</h3>
            <div className="info-grid">
              {campos.map((campo) => {
                const valor = formatarValor(campo, atendimento[campo]);
                const isLongText = typeof valor === 'string' && valor.length > 100;
                return (
                  <div key={campo} className={isLongText ? 'info-item full-width' : 'info-item'}>
                    <label>{getLabel(campo)}</label>
                    <span className={isLongText ? 'long-text' : ''}>{valor}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      </>
      )}

      {/* Aba: Exames */}
      {abaAtiva === 'exames' && atendimento.EXAMES && (
      <div className="detalhes-grid">
        {/* Exames do Atendimento */}
        {atendimento.EXAMES && (
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>🧪 Exames Solicitados ({atendimento.EXAMES ? atendimento.EXAMES.replace(/[\r\n]+/g, ' ').split(/\s{2,}/).filter(e => e.trim()).length : 0})</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Exame</th>
                  </tr>
                </thead>
                <tbody>
                  {atendimento.EXAMES
                    .replace(/[\r\n]+/g, ' ')
                    .split(/\s{2,}/)
                    .filter(e => e.trim())
                    .map((exame, idx) => (
                      <tr key={idx}>
                        <td style={{ width: '60px', textAlign: 'center' }}>{idx + 1}</td>
                        <td>{exame.trim()}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Aba: Guias */}
      {abaAtiva === 'guias' && atendimento.guias && atendimento.guias.length > 0 && (
      <div className="detalhes-grid">
        {/* Guias do Atendimento */}
        {atendimento.guias && atendimento.guias.length > 0 && (
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>📄 Guias ({atendimento.guias.length})</h3>
            <div className="table-responsive">
              <table className="data-table compact-table">
                <thead>
                  <tr>
                    <th>Numeração</th>
                    <th>Convênio</th>
                    <th>Plano</th>
                    <th>Carteirinha</th>
                    <th>Titular</th>
                    <th>Data Pedido</th>
                    <th>CID</th>
                  </tr>
                </thead>
                <tbody>
                  {atendimento.guias.map((guia, idx) => (
                    <React.Fragment key={idx}>
                      <tr>
                        <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                          <strong>{guia.NUMERACAO || '-'}</strong>
                        </td>
                        <td>{guia.CONVENIO_NOME || guia.CONVENIO || '-'}</td>
                        <td>{guia.PLANO || '-'}</td>
                        <td>{guia.NUMCARTEIRA || '-'}</td>
                        <td>{guia.TITULAR || '-'}</td>
                        <td>{formatDate(guia.DATAPEDIDO)}</td>
                        <td rowSpan="2" style={{ verticalAlign: 'top', paddingTop: '16px' }}>
                          {guia.CID || '-'}
                        </td>
                      </tr>
                      <tr>
                        <td>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Nº Guia:</span>{' '}
                          {guia.NUMEROGUIA || '-'}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Guia Op.:</span>{' '}
                          {guia.GUIA_OPERADORA || '-'}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Autorização:</span>{' '}
                          {guia.AUTORIZACAO || '-'}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Val. Carteira:</span>{' '}
                          {formatDate(guia.VALIDADE_CARTEIRA)}
                        </td>
                        <td>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>Faturar Em:</span>{' '}
                          {formatDate(guia.FATURAEM)}
                        </td>
                      </tr>
                      {guia.INDIC_CLINICA && (
                        <tr style={{ background: '#f9fafb' }}>
                          <td colSpan="7" style={{ padding: '0.5rem', fontSize: '0.85rem' }}>
                            <strong>Indicação Clínica:</strong> {guia.INDIC_CLINICA}
                          </td>
                        </tr>
                      )}
                      {guia.OBS_FATURAMENTO && (
                        <tr style={{ background: '#fef3c7' }}>
                          <td colSpan="7" style={{ padding: '0.5rem', fontSize: '0.85rem' }}>
                            <strong>⚠️ Obs. Faturamento:</strong> {guia.OBS_FATURAMENTO}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Aba: Pagamentos */}
      {abaAtiva === 'pagamentos' && (
      <div className="detalhes-grid">
        <div className="info-card" style={{ gridColumn: '1 / -1' }}>
          <h3>💰 Pagamentos ({atendimento.pagamentos?.length || 0})</h3>
          {atendimento.pagamentos && atendimento.pagamentos.length > 0 ? (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Ordem</th>
                    <th>Forma Pagamento</th>
                    <th>Data</th>
                    <th>Hora</th>
                    <th>Valor</th>
                    <th>Arred.</th>
                    <th>Usuário</th>
                  </tr>
                </thead>
                <tbody>
                  {atendimento.pagamentos.map((pag, idx) => (
                    <tr key={idx}>
                      <td>{pag.ORDEM || '-'}</td>
                      <td>{pag.FORMA_PAGAMENTO || '-'}</td>
                      <td>{formatDate(pag.DATA)}</td>
                      <td>{formatTime(pag.HORA)}</td>
                      <td><strong>R$ {(pag.VALOR || 0).toFixed(2)}</strong></td>
                      <td>{pag.ARREDONDAMENTO != null ? `R$ ${Number(pag.ARREDONDAMENTO).toFixed(2)}` : '-'}</td>
                      <td>{pag.USUARIO || '-'}</td>
                    </tr>
                  ))}
                  <tr style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold' }}>
                    <td colSpan="4" style={{ textAlign: 'right' }}>Total Pago:</td>
                    <td>R$ {atendimento.estatisticas?.valorTotalPago?.toFixed(2) || '0.00'}</td>
                    <td colSpan="2">
                      {atendimento.estatisticas?.statusPagamento && (() => {
                        const statusMap = {
                          PAGO: { label: 'Pago', bg: '#10b981' },
                          NAO_PAGO: { label: 'Nao Pago', bg: '#ef4444' },
                          PARCIAL: { label: 'Parcial', bg: '#f59e0b' },
                          EXCEDENTE: { label: 'Excedente', bg: '#8b5cf6' },
                          ISENTO: { label: 'Isento', bg: '#6b7280' }
                        };
                        const s = statusMap[atendimento.estatisticas.statusPagamento] || statusMap.NAO_PAGO;
                        return <span style={{ background: s.bg, color: '#fff', padding: '2px 8px', borderRadius: '8px', fontSize: '0.75rem' }}>{s.label}</span>;
                      })()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>💸</div>
              <p>Nenhum pagamento registrado</p>
              {atendimento.TOTAL > 0 && (
                <p style={{ color: '#ef4444', fontWeight: 'bold' }}>
                  Valor a pagar: R$ {(atendimento.TOTAL || 0).toFixed(2)}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Aba: Comunicações */}
      {abaAtiva === 'comunicacoes' && atendimento.comunicacoes && atendimento.comunicacoes.length > 0 && (
      <div className="detalhes-grid">
        {/* Comunicações do Atendimento */}
        {atendimento.comunicacoes && atendimento.comunicacoes.length > 0 && (
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>💬 Comunicações ({atendimento.comunicacoes.length})</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Data/Hora</th>
                    <th>Usuário</th>
                    <th>Computador</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {atendimento.comunicacoes.map((com, idx) => (
                    <tr key={idx}>
                      <td>{formatDate(com.DATA_HORA)}</td>
                      <td>{com.USUARIO || '-'}</td>
                      <td>{com.COMPUTADOR || '-'}</td>
                      <td>{com.OBSERVACAO || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}

      {/* Aba: Laudos */}
      {abaAtiva === 'laudos' && atendimento.laudos && atendimento.laudos.length > 0 && (
      <div className="detalhes-grid">
        {/* Laudos Armazenados */}
        {atendimento.laudos && atendimento.laudos.length > 0 && (
          <div className="info-card" style={{ gridColumn: '1 / -1' }}>
            <h3>📑 Laudos Armazenados ({atendimento.laudos.length})</h3>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID Laudo</th>
                    <th>Banco de Laudos</th>
                  </tr>
                </thead>
                <tbody>
                  {atendimento.laudos.map((laudo, idx) => (
                    <tr key={idx}>
                      <td><strong>{laudo.LAUDO_ARMAZENADO_ID || '-'}</strong></td>
                      <td>{laudo.NOME_BANCO_LAUDOS || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      )}
    </div>
  );
}

export default AtendimentoDetalhes;
